import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { listHomePeople, listHomeProjects, listHomeTasks } from './home.service'

const makeListState = () => ({
    items: [],
    status: 'idle', // idle | loading | loaded | loadingMore | error
    error: '',
    total: 0,
    hasNext: true,
    skip: 0,
})

const initialState = {
    pageLimit: 12,
    projects: makeListState(),
    people: makeListState(),
    tasksByTab: {
        upcoming: makeListState(),
        overdue: makeListState(),
        completed: makeListState(),
    },
}

export const fetchHomeProjects = createAsyncThunk(
    'homeSlice/fetchHomeProjects',
    async (
        { reset = false, limit = initialState.pageLimit } = {},
        { getState, rejectWithValue }
    ) => {
        const { homeSlice } = getState()
        const requestSkip = reset ? 0 : homeSlice?.projects?.skip || 0

        try {
            const response = await listHomeProjects({
                skip: requestSkip,
                limit,
            })
            return {
                reset,
                requestSkip,
                limit,
                data: response?.data || {},
            }
        } catch (error) {
            return rejectWithValue({
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    'Unable to load projects',
                reset,
            })
        }
    },
    {
        condition: ({ reset = false } = {}, { getState }) => {
            const { homeSlice } = getState()
            const status = homeSlice?.projects?.status
            if (status === 'loading' || status === 'loadingMore') return false
            if (!reset && !homeSlice?.projects?.hasNext) return false
            return true
        },
    }
)

export const fetchHomePeople = createAsyncThunk(
    'homeSlice/fetchHomePeople',
    async (
        { reset = false, limit = initialState.pageLimit } = {},
        { getState, rejectWithValue }
    ) => {
        const { homeSlice } = getState()
        const requestSkip = reset ? 0 : homeSlice?.people?.skip || 0

        try {
            const response = await listHomePeople({
                skip: requestSkip,
                limit,
            })
            return {
                reset,
                requestSkip,
                data: response?.data || {},
            }
        } catch (error) {
            return rejectWithValue({
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    'Unable to load people',
            })
        }
    },
    {
        condition: ({ reset = false } = {}, { getState }) => {
            const { homeSlice } = getState()
            const status = homeSlice?.people?.status
            if (status === 'loading' || status === 'loadingMore') return false
            if (!reset && !homeSlice?.people?.hasNext) return false
            return true
        },
    }
)

export const fetchHomeTasks = createAsyncThunk(
    'homeSlice/fetchHomeTasks',
    async (
        { tab = 'upcoming', reset = false, limit = initialState.pageLimit } = {},
        { getState, rejectWithValue }
    ) => {
        const normalizedTab = String(tab).toLowerCase()
        const { homeSlice } = getState()
        const currentTabState = homeSlice?.tasksByTab?.[normalizedTab] || makeListState()
        const requestSkip = reset ? 0 : currentTabState.skip || 0

        try {
            const response = await listHomeTasks({
                skip: requestSkip,
                limit,
            })
            const data = response?.data?.result || {}
            const taskBucket = data?.task?.[normalizedTab] || {}
            const supportTaskBucket = data?.supportTask?.[normalizedTab] || {}
            const taskData = Array.isArray(taskBucket?.data) ? taskBucket.data : []
            const supportTaskData = Array.isArray(supportTaskBucket?.data)
                ? supportTaskBucket.data
                : []
            const mergedItems = [
                ...taskData.map((item) => ({ ...item, taskSource: 'task' })),
                ...supportTaskData.map((item) => ({
                    ...item,
                    taskSource: 'supportTask',
                })),
            ]
            const total =
                (Number(taskBucket?.total) || 0) +
                (Number(supportTaskBucket?.total) || 0)
            const hasNext = !!taskBucket?.isNext || !!supportTaskBucket?.isNext

            return {
                tab: normalizedTab,
                reset,
                requestSkip,
                items: mergedItems,
                total,
                hasNext,
            }
        } catch (error) {
            return rejectWithValue({
                tab: normalizedTab,
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    'Unable to load tasks',
            })
        }
    },
    {
        condition: ({ tab = 'upcoming', reset = false } = {}, { getState }) => {
            const normalizedTab = String(tab).toLowerCase()
            const { homeSlice } = getState()
            const tabState = homeSlice?.tasksByTab?.[normalizedTab]
            if (!tabState) return false
            if (tabState.status === 'loading' || tabState.status === 'loadingMore')
                return false
            if (!reset && !tabState.hasNext) return false
            return true
        },
    }
)

const homeSlice = createSlice({
    name: 'homeSlice',
    initialState,
    reducers: {
        resetHomeProjectsState: (state) => {
            state.projects = makeListState()
        },
        resetHomePeopleState: (state) => {
            state.people = makeListState()
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHomeProjects.pending, (state, action) => {
                const reset = !!action?.meta?.arg?.reset
                state.projects.status =
                    reset || state.projects.status === 'idle' ? 'loading' : 'loadingMore'
                if (reset) {
                    state.projects.error = ''
                    state.projects.hasNext = true
                    state.projects.skip = 0
                }
            })
            .addCase(fetchHomeProjects.fulfilled, (state, action) => {
                const payload = action.payload || {}
                const data = payload?.data || {}
                const result = Array.isArray(data?.result) ? data.result : []
                const total = Number(data?.total) || 0
                const nextSkip = payload.requestSkip + result.length
                const hasNext =
                    typeof data?.isNext === 'boolean'
                        ? data.isNext
                        : nextSkip < total

                state.projects.items =
                    payload.reset === true
                        ? result
                        : [...state.projects.items, ...result]
                state.projects.total = total
                state.projects.hasNext = hasNext
                state.projects.skip = nextSkip
                state.projects.error = ''
                state.projects.status = 'loaded'
            })
            .addCase(fetchHomeProjects.rejected, (state, action) => {
                state.projects.status = 'error'
                state.projects.error =
                    action?.payload?.message ||
                    action?.error?.message ||
                    'Unable to load projects'
            })
            .addCase(fetchHomePeople.pending, (state, action) => {
                const reset = !!action?.meta?.arg?.reset
                state.people.status =
                    reset || state.people.status === 'idle' ? 'loading' : 'loadingMore'
                if (reset) {
                    state.people.error = ''
                    state.people.hasNext = true
                    state.people.skip = 0
                }
            })
            .addCase(fetchHomePeople.fulfilled, (state, action) => {
                const payload = action.payload || {}
                const data = payload?.data || {}
                const result = Array.isArray(data?.result) ? data.result : []
                const total = Number(data?.total) || 0
                const nextSkip = payload.requestSkip + result.length
                const hasNext =
                    typeof data?.isNext === 'boolean'
                        ? data.isNext
                        : nextSkip < total

                state.people.items =
                    payload.reset === true ? result : [...state.people.items, ...result]
                state.people.total = total
                state.people.hasNext = hasNext
                state.people.skip = nextSkip
                state.people.error = ''
                state.people.status = 'loaded'
            })
            .addCase(fetchHomePeople.rejected, (state, action) => {
                state.people.status = 'error'
                state.people.error =
                    action?.payload?.message ||
                    action?.error?.message ||
                    'Unable to load people'
            })
            .addCase(fetchHomeTasks.pending, (state, action) => {
                const tab = String(action?.meta?.arg?.tab || 'upcoming').toLowerCase()
                const reset = !!action?.meta?.arg?.reset
                const tabState = state.tasksByTab[tab]
                if (!tabState) return
                tabState.status =
                    reset || tabState.status === 'idle' ? 'loading' : 'loadingMore'
                if (reset) {
                    tabState.error = ''
                    tabState.hasNext = true
                    tabState.skip = 0
                }
            })
            .addCase(fetchHomeTasks.fulfilled, (state, action) => {
                const payload = action.payload || {}
                const tab = payload.tab
                const tabState = state.tasksByTab[tab]
                if (!tabState) return
                const nextSkip = payload.requestSkip + payload.items.length
                tabState.items =
                    payload.reset === true
                        ? payload.items
                        : [...tabState.items, ...payload.items]
                tabState.total = payload.total
                tabState.hasNext = payload.hasNext
                tabState.skip = nextSkip
                tabState.error = ''
                tabState.status = 'loaded'
            })
            .addCase(fetchHomeTasks.rejected, (state, action) => {
                const tab = String(action?.payload?.tab || 'upcoming').toLowerCase()
                const tabState = state.tasksByTab[tab]
                if (!tabState) return
                tabState.status = 'error'
                tabState.error =
                    action?.payload?.message ||
                    action?.error?.message ||
                    'Unable to load tasks'
            })
    },
})

export default homeSlice.reducer

export const homeState = (state) => {
    const { homeSlice: home = initialState } = state
    return {
        projects: home.projects.items,
        projectsStatus: home.projects.status,
        projectsError: home.projects.error,
        projectsTotal: home.projects.total,
        projectsHasNext: home.projects.hasNext,
        people: home.people.items,
        peopleStatus: home.people.status,
        peopleError: home.people.error,
        peopleTotal: home.people.total,
        peopleHasNext: home.people.hasNext,
        tasksByTab: home.tasksByTab,
        pageLimit: home.pageLimit,
    }
}

export const { resetHomeProjectsState, resetHomePeopleState } = homeSlice.actions

